import { useMemo, useState } from 'react';
import FormattedList from 'I18n/components/FormattedList';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Box } from 'foundations-components/transitional/components/Box';
import { CardGrid } from 'foundations-components/transitional/components/CardGrid';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import styled, { useTheme } from 'foundations-theming/utils';
import ContentOverview from './ContentOverview';
import { SkeletonBlock, SkeletonWrapper } from '../Skeleton';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { useFullTrack } from '../../features/tracks';
import { MOST_POPULAR_CERT_PROD, RECOMMENDED_FOR } from '../../features/certificates/certificatesConstants';
import { LearningContentCard } from '../../features/cards';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TRANSLATED_IDS = [23, 24, 29, 1028124, 15, 117, 11, 25, 9, 5, 12, 116, 36, 66, 19, 97, 87, ...MOST_POPULAR_CERT_PROD];
const isTranslatedIds = value => TRANSLATED_IDS.includes(value);
const CardGridHoverContainer = styled.div.withConfig({
  displayName: "ExpandedCertificate__CardGridHoverContainer",
  componentId: "ag5juc-0"
})(({
  isHovering,
  theme
}) => ({
  marginTop: theme.space['400'],
  marginBottom: theme.space['400'],
  padding: theme.space['200'],
  borderRadius: theme.borderRadius['200'],
  transition: 'background-color 200ms',
  backgroundColor: isHovering ? theme.color['fill-surface-recessed-hover'] : 'transparent',
  cursor: isHovering ? 'pointer' : 'default'
}));
const ExpandedCertificate = ({
  cardModel
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const {
    isMobile,
    isLaptop
  } = useMediaQuery();
  const theme = useTheme();
  const {
    track,
    trackLoading
  } = useFullTrack(cardModel.id, cardModel.language);
  const trackAggregatedLessonContents = useMemo(() => {
    var _track$lessons;
    return track === null || track === void 0 || (_track$lessons = track.lessons) === null || _track$lessons === void 0 ? void 0 : _track$lessons.reduce((aggregated, lesson) => {
      aggregated.push(...lesson.content);
      return aggregated;
    }, []);
  }, [track]);
  const handleMouseHover = flag => () => setIsHovering(flag);
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(CardGridHoverContainer, {
      "data-test-id": "expanded-certificate",
      isHovering: isHovering,
      children: /*#__PURE__*/_jsxs(CardGrid, {
        columns: isLaptop ? 1 : 2,
        gutterSize: isMobile ? 0 : undefined,
        onMouseEnter: handleMouseHover(true),
        onMouseLeave: handleMouseHover(false),
        children: [/*#__PURE__*/_jsx(LearningContentCard, {
          body: null,
          disableLink: true,
          isFlushOnMobile: false,
          item: cardModel,
          title: null,
          use: "no-hover"
        }), /*#__PURE__*/_jsxs(TFlex, {
          direction: "column",
          children: [/*#__PURE__*/_jsx(Text, {
            as: "h4",
            marginBlockEnd: "400",
            children: cardModel.name
          }), cardModel.description, !isMobile && /*#__PURE__*/_jsxs(_Fragment, {
            children: [RECOMMENDED_FOR[cardModel.id] && /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx(Text, {
                as: "h7",
                marginBlockEnd: "100",
                marginBlockStart: "300",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "learningCenter.mostPopularCertificates.recommendedFor"
                })
              }), /*#__PURE__*/_jsx(List, {
                marginBlockEnd: "200",
                paddingInlineStart: "400",
                styled: true,
                children: /*#__PURE__*/_jsx(FormattedList, {
                  children: RECOMMENDED_FOR[cardModel.id].map(persona => /*#__PURE__*/_jsx(FormattedMessage, {
                    message: `learningCenter.mostPopularCertificates.recommendedForRoles.${persona}`
                  }, `${cardModel.id}.${persona}`))
                })
              })]
            }), isTranslatedIds(cardModel.id) && /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx(Text, {
                as: "h7",
                marginBlockEnd: "100",
                marginBlockStart: "300",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "learningCenter.mostPopularCertificates.whatYouLearn"
                })
              }), /*#__PURE__*/_jsxs(List, {
                marginBlockEnd: "200",
                paddingInlineStart: "400",
                styled: true,
                children: [/*#__PURE__*/_jsx(ListItem, {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: `learningCenter.mostPopularCertificates.whatYouLearnBulletPoints.${cardModel.id}.1`
                  })
                }), /*#__PURE__*/_jsx(ListItem, {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: `learningCenter.mostPopularCertificates.whatYouLearnBulletPoints.${cardModel.id}.2`
                  })
                }), /*#__PURE__*/_jsx(ListItem, {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: `learningCenter.mostPopularCertificates.whatYouLearnBulletPoints.${cardModel.id}.3`
                  })
                })]
              })]
            }), /*#__PURE__*/_jsx(Box, {
              flexGrow: 1,
              inlineSize: "100%",
              children: /*#__PURE__*/_jsx(TFlex, {
                align: "end",
                blockSize: "100%",
                inlineSize: "100%",
                children: /*#__PURE__*/_jsxs(View, {
                  inlineSize: "100%",
                  children: [/*#__PURE__*/_jsx(Text, {
                    as: "h7",
                    marginBlock: "300",
                    children: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "learningCenter.mostPopularCertificates.includes"
                    })
                  }), trackLoading || !trackAggregatedLessonContents ? /*#__PURE__*/_jsx(View, {
                    inlineSize: "60%",
                    children: /*#__PURE__*/_jsx(SkeletonWrapper, {
                      children: /*#__PURE__*/_jsx(SkeletonBlock, {
                        color: "do-not-use-battleship",
                        height: theme.space['600'],
                        width: "100%"
                      })
                    })
                  }) : track && /*#__PURE__*/_jsx(ContentOverview, {
                    content: trackAggregatedLessonContents,
                    hasCertificate: true,
                    hasStudyGuide: track && !!track.studyGuide
                  })]
                })
              })
            })]
          })]
        })]
      })
    })
  );
};
export default ExpandedCertificate;